import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../assets/css/analyze.scss';
import '../../assets/css/analyze-loading.scss';
import '../../assets/css/resultDetailsModal.scss';
import '../../assets/css/processing.scss';

import PageTitle from '../UC/PageTitle';

import MultipleImages from '../UC/MultipleImages';
import GalleryFilter from '../Pages/GalleryFilter';
import Gallery from './Gallery';
import { useTranslation } from '../../contexts/TranslationContext';
import AnalyzeEngineParams from '../UC/AnalyzeEngineParams';
import AnalyzeProductResults from '../UC/AnalyzeProductResults';
import Loader from '../UC/Loader';
import ResultDetailsModal from '../UC/ResultDetailsModal';
import ZoomModal from './ZoomModal';
import Compare from '../Modals/Compare';
import api from '../../utils/api';

import { useGlobalPopup } from '../../contexts/GlobalPopupContext';

import './../../config'
import { sortImagesByPriority } from '../../utils/imageSorting';

const POLLING_INTERVAL = 5000; // 5 seconds between requests
const MAX_POLLING_ATTEMPTS = 300; // Maximum 300 attempts (1500 seconds total)

/**
 * Analyze component - Main analysis workflow
 * 
 * The layout changes based on the state:
 * 1. Initial state: Single column layout with item selector and analysis options
 * 2. During analysis: Two-column layout with item on the left, processing status on the right
 * 3. Results state: Two-column layout with item on the left, results grid on the right
 */
export default function Analyze() {
  const { language } = useTranslation();
  const { id } = useParams(); // Get the ID from URL parameters
  const navigate = useNavigate();

  const { showDeleteConfirm, showError, showSuccess, showWarning, showConfirm } = useGlobalPopup();

  // States
  const [showGallery, setShowGallery] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tempSelectedItem, setTempSelectedItem] = useState(null); // Temporary selection for gallery modal
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedImageAnalyze, setSelectedImageAnalyze] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisStat, setAnalysisStat] = useState(null);
  const [error, setError] = useState(null);
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false); // Track URL-based loading
  const [searchFilters, setSearchFilters] = useState({
    Make_IDs: [],
    ShoeTypes: [],
    Models: [],
    ProductCode: "",
  });
  const [progress, setProgress] = useState(0);
  const [isAborting, setIsAborting] = useState(false);
  const processIdRef = useRef(null);
  const abortTimeoutRef = useRef(null);
  const [historyFilters, setHistoryFilters] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState(''); // For showing detailed status messages
  const [showEngineParams, setShowEngineParams] = useState(false); // Show engine params with Alt+Shift+D

  const svgIconAnalyze = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <line x1="16.65" y1="16.65" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

    const svgIconHistory = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );

  const [engineParams, setEngineParams] = useState({
    TopResult: 100,
    TopGrouping: 100
  });

  // Keyboard event handler for Alt+Shift+D to toggle engine parameters
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.shiftKey && event.code === 'KeyD') {
        event.preventDefault();
        setShowEngineParams(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Handle selection from Gallery
  const handleItemSelect = (item) => {
    setTempSelectedItem(item); // Only set temporary selection, not the actual selected item
  };

  // Handle confirm selection from gallery modal
  const handleConfirmSelection = () => {
    if (tempSelectedItem) {
      setSelectedItem(tempSelectedItem);
      setSelectedImageIndex(0); // Reset to first image
    }
    setShowGallery(false);
    setTempSelectedItem(null); // Clear temporary selection
  };

  // Handle cancel selection from gallery modal
  const handleCancelSelection = () => {
    setShowGallery(false);
    setTempSelectedItem(null); // Clear temporary selection
  };  // Start analysis process
  const startAnalysis = async () => {
    
    if (!selectedItem) {
      setError(language.messages?.pleaseSelectItem || 'Please select an item to analyze');
      return;
    }

    try {
      setError(null);
      setIsAnalyzing(true);
      setAnalysisStatus('Starting analysis process...');
      setProgress(10);

      let searchInfo = GetAnalyzeSearchInfo();

      // Example API call - replace with your actual API endpoint
      const response = await api.post(`${global.config.api.domain}/fwiq-api/api/search1n/sendrequest`, {
        SearchInfo: searchInfo
      });

      if (response.status !== 200) {
        throw new Error(`Analysis failed with status: ${response.status}`);
      }
      const data = response.data;

      // Response returns ID of the background process, use this to poll for results
      if (data && data.ID) {
        processIdRef.current = data.ID;
        setAnalysisStatus('Processing analysis...');
        pollForResults(data.ID);
        return;
      }


    } catch (err) {
      // console.error('Analysis error:', err);
      setError(`Analysis failed: ${err.message}`);
      setIsAnalyzing(false); // Only set to false on error
    }
    // Remove the finally block - we'll handle setting isAnalyzing to false in the respective success/error handlers
  };

  // Add the filter change handler (stable reference to prevent GalleryFilter re-renders)
  const handleFilterChange = useCallback((newFilters) => {
    setSearchFilters(newFilters);
  }, []);



  // Update the GetAnalyzeSearchInfo function
  function GetAnalyzeSearchInfo() {
    let searchInfo = {
      ClientIp: "",
      Type: selectedItem.Type,
      Buckets: ["Catalog"], // optional: catalog | evidence
      EngineVersion: engineParams.Version || "", // Always use engine params
      CandidateSize: engineParams.TopResult || 100,
      MinScore: 1,
      TimeoutInSeconds: 0,
      SearchFilterMode: "like",
    };

    // selectedImageIndex is from arrStandardized

    let arrStandardized = sortAndFiltersImages(selectedItem.Images || []);
    try {
      searchInfo.Image_ID = arrStandardized[selectedImageIndex].IDValue;
      searchInfo.Type = arrStandardized[selectedImageIndex].Type || selectedItem.Type;
    } catch {
      searchInfo.Image_ID = selectedItem.Images[0].IDValue;
      searchInfo.Type = selectedItem.Type;
    }

    // searchInfo.Image_ID = selectedImageAnalyze.IDValue;

    searchInfo.Make_IDs = searchFilters.Make_IDs || [];
    searchInfo.ShoeTypesLike = searchFilters.ShoeTypes || [];
    searchInfo.ModelsLike = searchFilters.Models || [];
    searchInfo.ProductCode = searchFilters.ProductCode || "";

    // Always use engine params regardless of visibility
    searchInfo.EngineParams = JSON.stringify({
      SMode: engineParams.SMode || "1",
      DeltaX: engineParams.DeltaX || 20,
      DeltaY: engineParams.DeltaY || 20,
      Method: engineParams.Method || "1",
    });

    

    if (engineParams && engineParams.EnableQuickSearch && engineParams.QuickSearchVersion) {
      searchInfo.QuickSearch = {
        EngineVersion: engineParams.QuickSearchVersion || "",
        Top: engineParams.TopQuickSearch || 1000
      };
    }
    else {
      searchInfo.QuickSearch = null;
    }

    return searchInfo;
  }

  const handleSelectRecordChangeImage = (index, imgInfo) => {

    // let findIndex = selectedItem.Images.findIndex(img => img.IDValue === imgInfo.IDValue);

    // if (imgInfo)
    //   selectedImageAnalyze(imgInfo);

    setSelectedImageIndex(index);
  }

  // Add this function after other handler functions
  const handleAbortAnalysis = async () => {
    if (!processIdRef.current) return;

    setIsAborting(true);
    try {
      const response = await api.post(
        `${global.config.api.domain}/fwiq-api/api/search1n/cancel?id=${processIdRef.current}`
      );

      if (response.status !== 200) {
        throw new Error(`Failed to cancel analysis: ${response.status}`);
      }

      setError(language.messages?.analysisCancelled || 'Analysis cancelled');
    } catch (err) {
      setError(`Failed to cancel analysis: ${err.message}`);
    } finally {
      setIsAnalyzing(false);
      setIsAborting(false);
      processIdRef.current = null;
      if (abortTimeoutRef.current) {
        clearTimeout(abortTimeoutRef.current);
        abortTimeoutRef.current = null;
      }

      // show no record
      setAnalysisResults([]);
      setProgress(0);
      
    }
  };  // State for product details modal
  const [selectedProduct, setSelectedProduct] = useState(null); const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  // State for zoom modal
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  // State for compare modal
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  // Handle result selection - either open modal or navigate to details
  const handleResultSelect = (product, action) => {


    // Check if this is a compare action
    if (action === 'compare') {
      // only load standardized images for compare
      product.Images = product.Images.filter(img => img.IsStandardize === 1);
      if (product.Images.length === 0) {
        showWarning(
          language.analyze?.noStandardizedImageForCompare || "No standardized images available for comparison.",
        );
        return;
      }
      if (!selectedProduct || selectedProduct.ID !== product.ID) {
        setSelectedProduct(product);
      }

      // Set the compared product and open compare modal
      setCompareModalOpen(true);
    } else {
      setSelectedProduct(product);
      // Default behavior - show product details modal
      setIsProductModalOpen(true);
    }
  };

  // Handle zoom image
  const handleZoomImage = () => {
    // only zoom standardized image
    if (selectedItem && selectedItem.Images && selectedItem.Images.filter(img => img.IsStandardize === 1).length > 0) {
      setIsZoomModalOpen(true);
    }
  };
  // Close product details modal
  const handleCloseProductModal = () => {
    setIsProductModalOpen(false);
    setSelectedProduct(null);
  };
  // Close zoom modal
  const handleCloseZoomModal = () => {
    setIsZoomModalOpen(false);
  };

  // Close compare modal
  const handleCloseCompare = () => {
    setCompareModalOpen(false);
  };

  // Function to load probe image data by analysis ID
  const loadProbeImageById = useCallback(async (analysisId) => {
    try {
      const requestBody = {};
      let timezoneOffset = new Date().getTimezoneOffset() * -1;

      requestBody.IDs = [analysisId];

      const response = await api.post(
        `${global.config.api.domain}/fwiq-api/api/search1n/history/search?numberOfRecordsPerPage=1&pageNumber=1&timezoneoffset=${timezoneOffset}`,
        requestBody
      );

      const result = response.data;

      // the data of Probe is the first item
      let historyRecordsData = result;

      if (!historyRecordsData || !historyRecordsData.Records || !Array.isArray(historyRecordsData.Records)) {
        console.warn('History search returned unexpected data structure:', result);
        return null;
      }

      // Find the record that matches our analysis ID
      const matchingRecord = historyRecordsData.Records.find(record =>
        record.ID === analysisId || record.Id === analysisId
      );

      

      // filter of this Probe
      const historyFilters = matchingRecord ? matchingRecord.CustomFilters : null;

      if (matchingRecord && result.ImageUrls && Array.isArray(result.ImageUrls)) {
        // Find the matching image
        const imageOfProbe = result.ImageUrls.find(image => {
          return image.Image_ID === matchingRecord.CustomFilters?.Image_ID;
        });

        const requestForMetaOfProbe = await api.post(`${global.config.api.domain}/fwiq-api/api/evidence/search`, {
          PageNumber: 1,
          PageSize: 2,
          IDs: [imageOfProbe?.Product_ID],
          IncludeDeleted: true
        });

        const metaResult = requestForMetaOfProbe.data;

        let metaOfProbe = null;
        let imageProbeSelected = null;
        if (metaResult && metaResult.Records && Array.isArray(metaResult.Records) && metaResult.Records.length > 0) {
          metaOfProbe = metaResult.Records[0];
        }

        if (metaOfProbe) {
          imageProbeSelected = metaOfProbe.Images.find(img => img.IDValue === imageOfProbe?.Image_ID);
        }

        historyFilters.Models = historyFilters.ModelsLike;
        historyFilters.ShoeTypes = historyFilters.ShoeTypesLike;

        if (imageOfProbe) {
          // Return the probe data and history filters instead of setting state directly
          return {
            probeData: {
              ID: analysisId,
              Make: matchingRecord.Make || (language.messages?.loadedAnalysis || 'Loaded Analysis'),
              Model: matchingRecord.Model || `ID: ${analysisId}`,
              Type: imageProbeSelected.Type || matchingRecord.Type || (language.messages?.unknown || 'Unknown'),
              ImpressionInfo: {
                CaseIncident: metaOfProbe?.ImpressionInfo?.CaseIncident || '',
                ImpressionNumber: metaOfProbe?.ImpressionInfo?.ImpressionNumber || ''
              },
              Meta: metaOfProbe,
              Images: [{
                IDValue: matchingRecord.CustomFilters?.Image_ID || analysisId,
                ImagePath: imageOfProbe.RelativePath,
                S3RelativePath: imageOfProbe.RelativePath, // Keep as relative path for MultipleImages component
                S3OriginalRelativePath: imageOfProbe.Original?.RelativePath || '',
                Type: imageProbeSelected?.Type || (language.messages?.unknown || 'Unknown'),
                IsStandardize: 1
              }]
            },
            historyFilters: historyFilters
          };
        }
      }

      // Return null if no data found
      return null;
    } catch (error) {
      console.error('Error loading probe image:', error);
      // Return null if error occurs
      return null;
    }
  }, [language.messages?.loadedAnalysis]);

  // Separate polling function for reuse
  const pollForResults = useCallback(async (analysisId, attempts = 0) => {
    if (attempts >= MAX_POLLING_ATTEMPTS) {
      setError('Analysis timed out. Please try again.');
      setIsAnalyzing(false);
      setIsLoadingFromUrl(false);
      setProgress(0);
      return;
    }

    try {
      const newProgress = Math.min(20 + Math.round((attempts / MAX_POLLING_ATTEMPTS) * 60), 80);
      setProgress(newProgress);

      const response = await api.get(
        `${global.config.api.domain}/fwiq-api/api/search1n/getresponse?id=${analysisId}`
      );

      if (response.status !== 200) {
        throw new Error(`Failed to fetch results with status: ${response.status}`);
      }

      // For BigInt parsing, use the response data directly since api utility handles this
      const resultData = response.data;

      if (resultData.StatusCode === 13) { // Completed
        setAnalysisStatus('Analysis complete!');
        setProgress(100);
        setAnalysisResults(resultData.SearchResult.Hits);
        let analyzeStats = resultData.SearchResult?.Stats;
        analyzeStats.TotalTimeMs = resultData.SearchResult?.TotalTimeMs || 0;
        setAnalysisStat(analyzeStats);
        // Clear loading states since we have results
        setIsAnalyzing(false);
        setIsLoadingFromUrl(false);
        processIdRef.current = null;
        return;
      } else if (resultData.StatusCode === 12) { // In Process
        setAnalysisStatus('Processing analysis...');
        setTimeout(() => pollForResults(analysisId, attempts + 1), POLLING_INTERVAL);
      } else if (resultData.StatusCode === 14) { // Error
        setError(`Analysis failed with an error. Please try starting a new analysis.`);
        setIsAnalyzing(false);
        setIsLoadingFromUrl(false);
        setProgress(0);
        processIdRef.current = null;
        return;
      } else if (resultData.StatusCode === 11) { // Not Found
        setError(`Analysis not found. It may have expired or been cancelled.`);
        setIsAnalyzing(false);
        setIsLoadingFromUrl(false);
        setProgress(0);
        processIdRef.current = null;
        return;
      } else {
        setError(`Analysis has unexpected status: ${resultData.StatusCode}. Please contact support if this persists.`);
        setIsAnalyzing(false);
        setIsLoadingFromUrl(false);
        setProgress(0);
        processIdRef.current = null;
        return;
      }
    } catch (err) {
      setError(`Failed to check analysis status: ${err.message}`);
      setIsAnalyzing(false);
      setIsLoadingFromUrl(false);
      setProgress(0);

      // terminate 
      processIdRef.current = null;
      return;
    }
  }, []);

  // Function to load analysis results by ID (similar to polling logic in startAnalysis)
  const loadResultsById = useCallback(async (analysisId) => {
    setIsLoadingFromUrl(true);
    setError(null);
    setAnalysisStatus('Loading analysis results...');
    setProgress(50);

    // Reset analysis state to ensure clean UI
    setAnalysisResults(null);
    setSelectedItem(null);
    setSelectedImageIndex(0);

    try {
      const response = await api.get(
        `${global.config.api.domain}/fwiq-api/api/search1n/getresponse?id=${analysisId}`
      );

      if (response.status !== 200) {
        throw new Error(`Failed to fetch results with status: ${response.status}`);
      }

      // For BigInt parsing, use the response data directly since api utility handles this
      const resultData = response.data;

      if (resultData.StatusCode === 13) { // Completed
        setAnalysisStatus('Results loaded successfully!');
        setProgress(100);
        setAnalysisResults(resultData.SearchResult.Hits);

        let analyzeStats = resultData.SearchResult?.Stats;

        analyzeStats.TotalTimeMs = resultData.SearchResult?.TotalTimeMs || 0;

        setAnalysisStat(analyzeStats);

        

        // Load probe image data from history API
        const probeResult = await loadProbeImageById(analysisId);

        let probeItem = {
          ID: analysisId,
          Make: '',
          Model: '',
          Type: '',
          Images: [{
            IDValue: analysisId,
            ImagePath: '',
            S3RelativePath: '',
            S3OriginalRelativePath: ''
          }]
        };

        // Use probe image data if available
        if (probeResult && probeResult.probeData) {
          probeItem = probeResult.probeData;

          // Load history filters into engine parameters if available
          if (probeResult.historyFilters && probeResult.historyFilters.EngineParams) {
            try {
              const parsedEngineParams = JSON.parse(probeResult.historyFilters.EngineParams);
              setEngineParams(prevParams => ({
                ...prevParams,
                ...parsedEngineParams,
                Version: probeResult.historyFilters.EngineVersion || prevParams.Version,
                TopResult: probeResult.historyFilters.CandidateSize || prevParams.TopResult,
                QuickSearchVersion: probeResult.historyFilters.QuickSearch?.EngineVersion || prevParams.QuickSearchVersion,
                TopQuickSearch: probeResult.historyFilters.QuickSearch?.Top || prevParams.TopQuickSearch,
                EnableQuickSearch: probeResult.historyFilters.QuickSearch == null ? false : true
              }));
            } catch (error) {
              console.error('Error parsing engine parameters from history:', error);
            }
          }

          // Set history filters for GalleryFilter to use
          if (probeResult.historyFilters) {
            setHistoryFilters(probeResult.historyFilters);
          }
        } else {
          // If probe information is also available in the API response, use it as fallback
          if (resultData.SearchResult && resultData.SearchResult.ProbeInfo) {
            const probe = resultData.SearchResult.ProbeInfo;

            probeItem = {
              ID: analysisId,
              Make: probe.Make || (language.messages?.loadedAnalysis || 'Loaded Analysis'),
              Model: probe.Model || `ID: ${analysisId}`,
              ImpressionInfo: {
                CaseIncident: probe.CaseIncident || '',
                ImpressionNumber: probe.ImpressionNumber || ''
              },
              Type: probe.Type || (language.messages?.unknown || 'Unknown'),
              Images: probe.Images || [{
                IDValue: probe.Image_ID || analysisId,
                ImagePath: probe.ImagePath || '',
                S3RelativePath: probe.ImagePath || '',
                S3OriginalRelativePath: probe.S3OriginalRelativePath || '',
                Type: probe.Type || (language.messages?.unknown || 'Unknown')
              }]
            };
          }
        }

        setSelectedItem(probeItem);        // Clear loading states since we have results
        setIsAnalyzing(false);
        setIsLoadingFromUrl(false);

      } else if (resultData.StatusCode === 12) { // In Process
        setAnalysisStatus('Analysis still in progress...');
        setIsAnalyzing(true);
        processIdRef.current = analysisId;

        // Start polling for this existing analysis
        // pollForResults(analysisId);
      } else if (resultData.StatusCode === 11) { // Not Found
        throw new Error(`Analysis with ID ${analysisId} not found. It may have expired or been deleted.`);
      } else if (resultData.StatusCode === 14) { // Error
        throw new Error(`Analysis failed with an error. Please try starting a new analysis.`);
      } else {
        throw new Error(`Analysis has unexpected status: ${resultData.StatusCode}. Please contact support if this persists.`);
      }
    } catch (err) {
      setError(`Failed to load analysis results: ${err.message}`);
      console.error('Error loading results by ID:', err);

      // Reset states on error
      setIsAnalyzing(false);
      setProgress(0);
      setAnalysisStatus('');
    } finally {
      // Only set loading to false if we're not starting to poll for in-progress analysis
      if (!processIdRef.current) {
        setIsLoadingFromUrl(false);
      }
    }
  }, [loadProbeImageById, language.messages?.loadedAnalysis, language.messages?.unknown]);

  // Effect to handle URL parameter on component mount
  useEffect(() => {
    if (id) {
      // If there's an ID in the URL, load the results for that ID
      loadResultsById(id);
    }
  }, [id, loadResultsById]);

  // Effect to handle record selection from gallery analyze button
  useEffect(() => {
    // Check if there's a selected record from the gallery analyze button
    const selectedRecordFromGallery = sessionStorage.getItem('selectedAnalyzeRecord');
    if (selectedRecordFromGallery && !id && !selectedItem) {
      try {
        const record = JSON.parse(selectedRecordFromGallery);
        setSelectedItem(record);
        setSelectedImageIndex(0); // Reset to first image
        // Clear the stored record after using it
        sessionStorage.removeItem('selectedAnalyzeRecord');
      } catch (error) {
        console.error('Error parsing selected analyze record from sessionStorage:', error);
        // Clear invalid data
        sessionStorage.removeItem('selectedAnalyzeRecord');
      }
    }
  }, [id, selectedItem]); // Include dependencies

  // Function to reset analysis state for new analysis
  const resetAnalysisState = () => {
    setAnalysisResults(null);
    setAnalysisStat(null);
    setIsAnalyzing(false);
    setIsLoadingFromUrl(false);
    setError(null);
    setProgress(0);
    setAnalysisStatus('');
    setHistoryFilters(null); // Clear history filters
    processIdRef.current = null;
    // Clear URL if we're on an ID-based route
    if (id) {
      navigate('/analyze', { replace: true });
    }
  };

  // Image sorting delegated to shared utility (isAnalyze=true → standardized only)
  const sortAndFiltersImages = (images) => sortImagesByPriority(images, true);

  return (
    <div className="analyze-container">
      {/* Always render EngineParams hidden to ensure Version is always loaded */}
      <div style={{ display: 'none' }}>
        <AnalyzeEngineParams
          params={engineParams}
          onChange={setEngineParams}
        />
      </div>

      <div className="analyze-header">
        {!id &&
          <>
            {/* <h1>{language.analyze?.title || 'Analyze'}</h1>
            <p>{language.analyze?.description || 'Select a footwear item to find matching retail gallery.'}</p> */}

            <PageTitle
              title={language.analyze?.pageTitle || 'Analyze'}
              subtitle={language.analyze?.pageDescription || 'Select a footwear item to find matching retail gallery.'}
              icon={svgIconAnalyze}
            />
          </>
        }
        {id &&
          // <h1>{language.analyze?.titleAnalyzeHistory || 'View Results'}</h1>
          <PageTitle
            title={language.analyze?.pageTitleAnalyzeHistory || 'View Results'}
            subtitle={language.analyze?.pageDescriptionAnalyzeHistory || 'View or modify previous analysis results.'}
            icon={svgIconHistory}
          />
        }

      </div>

      {/* Loading indicator for URL-based loading */}
      {/* {isLoadingFromUrl && (
        <div className="loading-from-url">
          <Loader size="medium" />
          <p>{analysisStatus || 'Loading analysis results...'}</p>
          <div className="progress-container">
            <div
              className="progress-bar"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
        </div>
      )} */}

      <div className="analyze-content">
        {/* Left Column - Always contains 3 sections */}
        <div className="left-column">
          {/* Selection Section */}
          <div className={`selection-section ${id ? 'with-id' : ''} ${selectedItem ? 'has-selection' : ''} ${isAnalyzing ? 'analyzing' : ''} ${analysisResults ? 'has-results' : ''}`}>
            <div className="selection-header">
              {!id && (
                <>
                  <h3>{language.analyze?.selectionTitle || 'Select Image'}</h3>

                  <button className="btn btn-primary open-gallery-btn" onClick={() => setShowGallery(true)}>
                    <i className="fa fa-search"></i> {language.analyze?.selectRecord || 'Select Record'}
                  </button>
                </>
              )}
            </div>

            {/* Selected Item Preview */}
            {selectedItem ? (
              <div className="selected-item">
                <div className="item-images">
                  <MultipleImages
                    key={selectedItem.ID || `${selectedItem.Make}-${selectedItem.Model}-${Date.now()}`}
                    images={sortAndFiltersImages(selectedItem.Images || [])}
                    handleZoomImage={handleZoomImage}
                    onImageChange={handleSelectRecordChangeImage}
                    isAnalyze={true}
                    defaultIndex={selectedImageIndex}
                    title={selectedItem.ImpressionInfo && selectedItem.ImpressionInfo.CaseIncident && selectedItem.ImpressionInfo.ImpressionNumber ? `${selectedItem.ImpressionInfo.CaseIncident} #${selectedItem.ImpressionInfo.ImpressionNumber}` : ""}
                  />
                </div>
                {!isAnalyzing && (
                  <div className="analysis-actions">
                    {/* Show different buttons based on whether this was loaded from URL */}
                    {id ? (
                      <>
                        <button
                          className="btn btn-success"
                          onClick={resetAnalysisState}
                        >
                          <i className="fa fa-plus"></i> {language.analyze?.startNewAnalysis || 'Start New Analysis'}
                        </button>
                        {/* <small className="loaded-from-url-notice">
                          <i className="fa fa-info-circle"></i> {language.analyze?.analysisLoadedFromUrl?.replace('{id}', id) || `Analysis loaded from URL (ID: ${id})`}
                        </small> */}
                      </>
                    ) : (
                      <button
                        className="btn btn-success"
                        onClick={startAnalysis}
                        disabled={!selectedItem}
                      >
                        <i className="fa fa-search"></i> {language.analyze?.startAnalysis || 'Start Analysis'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                {!id && (
                  <div className="no-selection">
                    <i className="fa fa-shoe-prints fa-3x"></i>
                    <p>{language.analyze?.noSelection || 'No item selected'}</p>
                    <p>{language.analyze?.selectPrompt || 'Please select an item from the gallery to proceed with analysis'}</p>
                  </div>
                )}
              </>
            )}

            {error && (
              <div className="error-message">
                <i className="fa fa-exclamation-circle"></i> {error}
              </div>
            )}
          </div>

          {/* Engine Parameters Section */}
          {selectedItem && showEngineParams && (
            <div className="engine-params-section">
              <h2>
                {language.analyze?.engineParamsTitle || 'Engine Parameters'}
                <span className="advanced-mode-indicator" title="Advanced Engine Parameters Active (Alt+Shift+D)">
                  {' '}<i className="fa fa-cog"></i>
                </span>
              </h2>
              <AnalyzeEngineParams
                params={engineParams}
                onChange={setEngineParams}
              />
            </div>
          )}

          {/* Filter Options Section */}
          {selectedItem && (
            <div className="filter-section">
              {id && (
                <h2>
                  {language.analyze?.historyFilterTitle || 'History Filter Options'}
                  {showEngineParams && (
                    <span className="advanced-mode-indicator" title="Advanced Engine Parameters Active (Alt+Shift+D)">
                      {' '}<i className="fa fa-cog"></i>
                    </span>
                  )}
                </h2>
              )}
              {!id && (
                <h2>
                  {language.analyze?.filterTitle || 'Filter Options'}
                  {showEngineParams && (
                    <span className="advanced-mode-indicator" title="Advanced Engine Parameters Active (Alt+Shift+D)">
                      {' '}<i className="fa fa-cog"></i>
                    </span>
                  )}
                </h2>
              )}
              <GalleryFilter
                galleryType="catalog"
                onFilterChange={handleFilterChange}
                selectedFilters={searchFilters}
                isAnalyze={true}
                showTopResult={!showEngineParams}
                historyFilters={historyFilters}
              />
            </div>
          )}
        </div>

        {/* Results Section - Only show when analyzing or has results */}
        {(isAnalyzing || analysisResults) && (
          <div className="results-section">
            <h2>{isAnalyzing ? (language.analyze?.processingTitle || 'Processing Analysis') : (language.analyze?.resultsTitle || 'Analysis Results')}</h2>

            {isAnalyzing ? (
              <div className="processing-container">
                <div className="processing-status">
                  <Loader size="medium" />
                  <h3>{analysisStatus}</h3>
                  <div className="progress-container large">
                    <div
                      className="progress-bar"
                      style={{ width: `${progress}%` }}
                      role="progressbar"
                      aria-valuenow={progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                  <p>{language.analyze?.waitMessage || 'Analysis in progress...'}</p>
                  <button
                    className="btn btn-danger btn-lg"
                    onClick={handleAbortAnalysis}
                    disabled={isAborting}
                  >
                    {isAborting ? (
                      <><i className="fa fa-spinner fa-spin"></i> {language.analyze?.cancelling || 'Cancelling...'}</>
                    ) : (
                      <><i className="fa fa-stop-circle"></i> {language.analyze?.cancelAnalysis || 'Cancel Analysis'}</>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <AnalyzeProductResults
                results={analysisResults}
                onResultSelect={handleResultSelect}
                probeItem={selectedItem}
                analysisStat={analysisStat}
              />
            )}
          </div>
        )}
      </div>

      {/* Gallery Modal */}
      {showGallery && (
        <div className="backdrop">
          <div className="modal gallery-selection-modal">
            <div className="modal-header">
              <h2>{language.analyze?.selectRecordAnalyze || 'Select Record to Analyze'}</h2>
              <button className="close-modal-btn" onClick={() => setShowGallery(false)}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="modal-content">
              <div className="gallery-container">

                {/* Wrap your Gallery component or implement the grid structure */}
                <Gallery
                  onClose={() => setShowGallery(false)}
                  onSelect={handleItemSelect}
                  selectionMode={true}
                />
              </div>
            </div>
            <div className="modal-footer">
              <div className="selection-summary" style={{ display: 'none' }}>
                {tempSelectedItem && (
                  <div className="temp-selection-preview">
                    <span>Selected: {tempSelectedItem.Make} {tempSelectedItem.Model}</span>
                  </div>
                )}
              </div>

              <button
                className="btn btn-primary"
                onClick={handleConfirmSelection}
                disabled={!tempSelectedItem}
              >
                <i className="fa fa-check"></i> {language.analyze?.confirm || 'Confirm Selection'}
              </button>
              <button className="btn btn-secondary" onClick={handleCancelSelection}>
                <i className="fa fa-times"></i>
                {language.analyze?.close || 'Close'}
              </button>
            </div>
          </div>
        </div>)}

      {/* Product Details Modal */}

      <ResultDetailsModal
        isOpen={isProductModalOpen}
        onClose={handleCloseProductModal}
        productDetails={selectedProduct}
      />      {/* Zoom Modal for images */}
      <ZoomModal
        isOpen={isZoomModalOpen}
        onClose={handleCloseZoomModal}
        images={selectedItem ? selectedItem.Images.filter(img => img.IsStandardize === 1) || [] : []}
        initialIndex={selectedImageIndex}
      />

      {/* Compare Modal */}
      <Compare
        isOpen={compareModalOpen}
        onClose={handleCloseCompare}
        resultItem={selectedProduct}
        probeItem={selectedItem}
      />
    </div>);
}